/* eslint-disable @typescript-eslint/no-explicit-any -- mock serve JSON dinâmico; any é pragmático aqui */
/**
 * Mock adapter para axios — serve src/mocks/db.json como se fosse a API.
 * Ativado quando VITE_USE_MOCK === 'true' (ver axiosClient.ts).
 * Switch fácil: liga/desliga pela env, sem tocar em endpoints/hooks.
 */
import type { AxiosAdapter, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import db from './db.json';
import {
  activeUsersWindows,
  buildFinance,
  buildFirebaseAnalytics,
  buildUptime,
} from './metrics.mock';
import {
  ME,
  novoIdMensagem,
  recalcularConversa,
  supportAgents,
  supportCanned,
  supportConversations,
  supportLabels,
  supportThreads,
} from './support.mock';
import {
  careerApplications,
  careerJobs,
  deviceInsights,
  notificacoes,
  orcamentoProdutos,
  orcamentosSalvos,
  permissoesDosCargos,
  teamMembers,
  teamRoles,
} from './admin.mock';
import { brCodeDeMentira, cobrancasExemplo, qrDeMentira, tokenDeMentira } from './cobrancas.mock';

const LATENCY_MS = 280; // simula rede pra spinners aparecerem
const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

// db mutável em memória (mutações resetam no reload da página)
const DB: any = JSON.parse(JSON.stringify(db));
// Coleções extras (arquivos *.mock.ts), também mutáveis em memória.
DB.team = structuredClone(teamMembers);
DB.jobs = structuredClone(careerJobs);
DB.applications = structuredClone(careerApplications);
DB.produtos = structuredClone(orcamentoProdutos);
DB.orcamentos = structuredClone(orcamentosSalvos);
DB.notifications = structuredClone(notificacoes);
DB.support = supportConversations;
DB.cobrancas = structuredClone(cobrancasExemplo);

function recorrenciaDeMentira() {
  const hoje = new Date();
  const dia = (d: Date) => d.toISOString().slice(0, 10);
  const venc = new Date(hoje.getTime() + 3 * 86400_000);
  return {
    ativa: true,
    assinatura: {
      status: 'active',
      origem: 'admin_custom',
      ciclo: 'mensal',
      current_period_end: venc.toISOString(),
      modo_cobranca: 'manual_link',
      cancelar_no_fim: false,
      cancelada_em: null,
      participa: true,
    },
    faturas: [
      {
        id: 1,
        periodo_inicio: dia(venc),
        periodo_fim: dia(new Date(venc.getTime() + 30 * 86400_000)),
        vencimento: dia(venc),
        valor_centavos: 3990,
        status: 'aberta',
        origem: 'job',
        paga_em: null,
        ciclo: 'mensal',
        cobranca_id: 6,
        cobranca_status: 'PENDENTE',
        metodo: null,
        baixa_manual_forma: null,
        pagamento_duplicado_em: null,
        link_pagamento: 'https://painel.curraltech.com/pagar/mock',
      },
    ],
    mensagens: [
      {
        id: 1,
        canal: 'whatsapp',
        template: 'cobranca_nova',
        status: 'entregue',
        tentativas: 1,
        erro: null,
        fatura_id: 1,
        agendar_para: nowIso(),
        created_at: nowIso(),
        updated_at: nowIso(),
      },
    ],
    preferencias: { whatsapp_cobranca: true, optout_em: null, optout_origem: null },
  };
}

const nowIso = () => new Date().toISOString();
const nextId = (list: any[]) => list.reduce((max, x) => Math.max(max, Number(x.id) || 0), 0) + 1;
const digits = (s: unknown) => String(s ?? '').replace(/\D/g, '');

function findConv(id: string | number) {
  return DB.support.find((c: any) => String(c.conversation.id) === String(id));
}

function supportMeta() {
  const count = (s: string) => DB.support.filter((c: any) => c.conversation.status === s).length;
  return {
    open_count: count('open'),
    pending_count: count('pending'),
    snoozed_count: count('snoozed'),
  };
}

// Mensagem enviada pelo atendente logado (texto, áudio ou anexo).
function pushOutgoing(conv: any, data: Record<string, unknown>) {
  const message = {
    id: novoIdMensagem(),
    message_type: 1,
    created_at: Math.floor(Date.now() / 1000),
    sender: { id: ME.id, name: ME.name, type: 'user' },
    status: 'sent',
    conversation_id: conv.conversation.id,
    ...data,
  };
  conv.messages.push(message);
  recalcularConversa(conv);
  return message;
}

// Card do produtor casado pelo telefone (usuários do db.json).
function producerByPhone(phone: string) {
  const alvo = digits(phone);
  const u = DB.users.find((x: any) => digits(x.phone) === alvo);
  if (!u) return { producer: null, activity: [] };
  const sub = DB.subscriptions[u.id];
  const a = sub?.assinatura;
  const plano = sub?.entitlement?.plano;
  const isProducer = u.role === 'owner';
  const producer = {
    id: Number(u.id),
    name: u.name,
    phone: u.phone,
    status: u.status,
    isProducer,
    animalCount: sub?.uso?.animais ?? 0,
    property: u.property
      ? { name: u.property.name, city: u.property.city, uf: u.property.uf }
      : null,
    assinatura:
      isProducer && a
        ? {
            status: a.status === 'comp' ? 'active' : a.status,
            origem: a.origem,
            plano: plano ? { nome: plano.nome, codigo: plano.codigo } : null,
            currentPeriodEnd: a.current_period_end ?? a.trial_ends_at ?? null,
          }
        : null,
    coberturaHerdada:
      !isProducer && plano
        ? { status: 'active', produtorNome: 'Proprietário da fazenda', plano }
        : null,
  };
  const dia = (d: number) => new Date(Date.now() - d * 86_400_000).toISOString();
  const activity = [
    { type: 'sync', title: 'Sincronizou o aplicativo', subtitle: u.lastDevice, at: dia(0) },
    {
      type: 'manejo',
      title: 'Registrou pesagem de 38 animais',
      subtitle: u.property?.name,
      at: dia(1),
    },
    { type: 'animal', title: 'Cadastrou 12 bezerros', subtitle: 'Lote Maternidade', at: dia(3) },
    {
      type: 'assinatura',
      title: plano ? `Plano ${plano.nome}` : 'Sem plano ativo',
      subtitle: a?.origem ? `Origem: ${a.origem}` : undefined,
      at: dia(9),
    },
  ];
  return { producer, activity };
}

function ok(data: unknown, config: InternalAxiosRequestConfig, status = 200): AxiosResponse {
  return { data, status, statusText: 'OK', headers: {}, config };
}
function fail(status: number, message: string, config: InternalAxiosRequestConfig): never {
  const err: any = new Error(message);
  err.response = { data: { message }, status, statusText: 'Error', headers: {}, config };
  err.isAxiosError = true;
  throw err;
}

function paginate<T>(items: T[], params: any, key: string) {
  const page = Number(params?.page ?? 1);
  const limit = Number(params?.limit ?? 20);
  const start = (page - 1) * limit;
  return { [key]: items.slice(start, start + limit), total: items.length, page, limit };
}

function qs(url: string): Record<string, string> {
  const q = url.split('?')[1];
  if (!q) return {};
  return Object.fromEntries(new URLSearchParams(q));
}

export const mockAdapter: AxiosAdapter = async (config: InternalAxiosRequestConfig) => {
  await delay(LATENCY_MS);
  const method = (config.method || 'get').toLowerCase();
  const rawUrl = config.url || '';
  const path = rawUrl.split('?')[0].replace(/\/+$/, '');
  const params = { ...qs(rawUrl), ...(config.params || {}) };
  const body = config.data
    ? typeof config.data === 'string'
      ? JSON.parse(config.data)
      : config.data
    : {};

  const m = (re: RegExp) => path.match(re);
  let mr: RegExpMatchArray | null;

  // ---------- AUTH ----------
  if (method === 'post' && path.endsWith('/manager/auth/login')) {
    if (!body.email || !body.password) fail(401, 'Email ou senha inválidos', config);
    return ok({ token: DB.auth.token, manager: DB.auth.manager }, config);
  }
  if (method === 'post' && path.endsWith('/manager/auth/logout')) return ok({}, config);
  if (method === 'get' && path.endsWith('/manager/auth/me'))
    return ok({ manager: DB.auth.manager }, config);

  // ---------- DASHBOARD ----------
  if (method === 'get' && path.endsWith('/dashboard/metrics'))
    return ok(DB.dashboard.metrics, config);
  if (method === 'get' && path.endsWith('/dashboard/device-activity')) {
    const map: any = { '7d': 7, '30d': 30, '3m': 90 };
    const n = map[params.period as string] ?? 7;
    const points = DB.dashboard.deviceActivity
      .slice(-n)
      .map((p: any) => ({ date: p.date, devices: p.peak_daily }));
    return ok(points, config);
  }
  if (method === 'get' && path.endsWith('/dashboard/errors-by-type'))
    return ok(DB.dashboard.errorsByType, config);
  if (method === 'get' && path.endsWith('/dashboard/recent-devices')) {
    const limit = Number(params.limit ?? 10);
    return ok(DB.dashboard.recentDevices.slice(0, limit), config);
  }

  // ---------- NOTIFICAÇÕES: insights de aparelhos (antes de /devices) ----------
  if (method === 'get' && path.endsWith('/manager/notifications/insights/devices'))
    return ok(deviceInsights, config);

  // ---------- DEVICES ----------
  if (method === 'get' && (mr = m(/\/devices\/([^/]+)$/))) {
    const d = DB.devices.find((x: any) => x.id === mr![1]);
    if (!d) fail(404, 'Device não encontrado', config);
    return ok(
      {
        ...d,
        deviceInfo: { manufacturer: d.platform === 'ios' ? 'Apple' : 'Android', locale: 'pt-BR' },
        performanceMetrics: {
          avgResponseTime: 120 + (d.errors % 200),
          memoryUsage: 40 + (d.sessions % 50),
          batteryLevel: 30 + (d.sessions % 70),
        },
      },
      config
    );
  }
  if (method === 'get' && path.endsWith('/devices')) {
    let list = [...DB.devices];
    if (params.platform) list = list.filter((d) => d.platform === params.platform);
    if (params.osVersion) list = list.filter((d) => d.osVersion === params.osVersion);
    if (params.status) list = list.filter((d) => d.status === params.status);
    return ok(paginate(list, params, 'devices'), config);
  }

  // ---------- ERRORS ----------
  if (method === 'patch' && (mr = m(/\/errors\/([^/]+)\/resolve$/))) {
    const e = DB.errors.find((x: any) => x.id === mr![1]);
    if (e) {
      e.status = 'resolved';
      e.resolvedAt = new Date().toISOString();
      e.resolvedByName = DB.auth.manager.name;
      e.notes = body.notes || '';
    }
    return ok({ success: true }, config);
  }
  if (method === 'get' && (mr = m(/\/errors\/([^/]+)\/affected-devices$/))) {
    return ok(DB.affectedDevices[mr[1]] || [], config);
  }
  if (method === 'get' && (mr = m(/\/errors\/([^/]+)$/))) {
    const e = DB.errors.find((x: any) => x.id === mr![1]);
    if (!e) fail(404, 'Erro não encontrado', config);
    return ok(e, config);
  }
  if (method === 'get' && path.endsWith('/errors')) {
    let list = [...DB.errors];
    if (params.status) list = list.filter((e) => e.status === params.status);
    if (params.severity) list = list.filter((e) => e.severity === params.severity);
    return ok(paginate(list, params, 'errors'), config);
  }

  // ---------- LOGS ----------
  if (method === 'post' && path.endsWith('/logs/export')) {
    const txt = DB.logs
      .map((l: any) => `${l.timestamp} [${l.level}] ${l.component}: ${l.message}`)
      .join('\n');
    return ok(new Blob([txt], { type: 'text/plain' }), config);
  }
  if (method === 'get' && path.endsWith('/logs')) {
    let list = [...DB.logs];
    if (params.deviceId) list = list.filter((l) => l.deviceId === params.deviceId);
    if (params.level) list = list.filter((l) => l.level === params.level);
    return ok(paginate(list, params, 'logs'), config);
  }

  // ---------- SETTINGS ----------
  if (method === 'get' && path.endsWith('/settings')) return ok(DB.settings, config);
  if (method === 'patch' && (mr = m(/\/settings\/([^/]+)$/))) {
    const sec = mr[1].replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    DB.settings[sec] = { ...(DB.settings[sec] || {}), ...body };
    return ok(DB.settings[sec], config);
  }

  // ---------- PLANS ----------
  if (method === 'get' && path.endsWith('/admin/plans')) return ok({ planos: DB.plans }, config);
  if (method === 'post' && path.endsWith('/admin/plans')) {
    const novo = {
      ...body,
      id: DB.plans.length + 1,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    DB.plans.push(novo);
    return ok({ plano: novo }, config, 201);
  }
  if (method === 'patch' && (mr = m(/\/admin\/plans\/([^/]+)$/))) {
    const p = DB.plans.find((x: any) => String(x.id) === mr![1]);
    if (p) Object.assign(p, body, { updated_at: new Date().toISOString() });
    return ok({ plano: p }, config);
  }

  // ---------- SUBSCRIPTION (por usuário) — antes de /admin/users/:id ----------
  if ((mr = m(/\/admin\/users\/([^/]+)\/subscription$/))) {
    const uid = mr[1];
    if (method === 'get') {
      const sub = DB.subscriptions[uid];
      if (!sub) fail(404, 'Sem assinatura', config);
      return ok(sub, config);
    }
    // POST ou PATCH
    const sub = DB.subscriptions[uid];
    const assinatura = {
      ...(sub?.assinatura || {}),
      ...body,
      updated_at: new Date().toISOString(),
    };
    if (sub) sub.assinatura = assinatura;
    return ok({ assinatura }, config, method === 'post' ? 201 : 200);
  }
  if (method === 'get' && (mr = m(/\/admin\/users\/([^/]+)\/funcionarios$/))) {
    return ok({ funcionarios: DB.funcionarios[mr[1]] || [] }, config);
  }
  if (method === 'post' && (mr = m(/\/admin\/users\/([^/]+)\/block$/))) {
    const u = DB.users.find((x: any) => x.id === mr![1]);
    if (u) {
      u.status = 'blocked';
      u.blockReason = body.reason;
      u.blockedAt = new Date().toISOString();
    }
    return ok({ success: true }, config);
  }
  if (method === 'post' && (mr = m(/\/admin\/users\/([^/]+)\/unblock$/))) {
    const u = DB.users.find((x: any) => x.id === mr![1]);
    if (u) {
      u.status = 'active';
      delete u.blockReason;
      delete u.blockedAt;
    }
    return ok({ success: true }, config);
  }
  if (method === 'post' && m(/\/admin\/users\/([^/]+)\/reset-password$/))
    return ok({ success: true }, config);

  // ---------- USERS ----------
  if (method === 'get' && (mr = m(/\/admin\/users\/([^/]+)$/))) {
    const u = DB.users.find((x: any) => x.id === mr![1]);
    if (!u) fail(404, 'Usuário não encontrado', config);
    return ok(u, config);
  }
  if (method === 'patch' && (mr = m(/\/admin\/users\/([^/]+)$/))) {
    const u = DB.users.find((x: any) => x.id === mr![1]);
    if (u) Object.assign(u, body);
    return ok(u || { success: true }, config);
  }
  if (method === 'delete' && (mr = m(/\/admin\/users\/([^/]+)$/))) {
    DB.users = DB.users.filter((x: any) => x.id !== mr![1]);
    return ok({ success: true }, config);
  }
  if (method === 'get' && path.endsWith('/admin/users')) {
    let list = [...DB.users];
    if (params.status) list = list.filter((u) => u.status === params.status);
    if (params.role) list = list.filter((u) => u.role === params.role);
    if (params.search) {
      const s = String(params.search).toLowerCase();
      list = list.filter((u) => u.name.toLowerCase().includes(s) || u.phone.includes(s));
    }
    return ok(paginate(list, params, 'users'), config);
  }
  if (method === 'post' && path.endsWith('/admin/users')) {
    const novo = {
      id: String(DB.users.length + 1),
      ...body,
      status: 'active',
      verified: false,
      createdAt: new Date().toISOString(),
      lastAccess: new Date().toISOString(),
    };
    DB.users.push(novo);
    return ok(novo, config, 201);
  }

  // ---------- DASHBOARD (Analytics + usuários ativos) ----------
  if (method === 'get' && path.endsWith('/dashboard/firebase-analytics'))
    return ok(buildFirebaseAnalytics(), config);
  if (method === 'get' && path.endsWith('/dashboard/active-users'))
    return ok(activeUsersWindows, config);

  // ---------- UPTIME ----------
  if (method === 'get' && path.endsWith('/manager/uptime')) return ok(buildUptime(), config);

  // ---------- FINANÇAS ----------
  if (method === 'get' && path.endsWith('/admin/finance'))
    return ok(buildFinance(params.period), config);

  // ---------- COBRANÇAS (AbacatePay) ----------
  if (method === 'get' && path.endsWith('/admin/cobrancas/config'))
    return ok({ configurado: true, loja: { nome: 'Curral Tech', saldo: null } }, config);
  if (method === 'get' && path.endsWith('/admin/cobrancas/precos')) {
    const precos = DB.plans.flatMap((p: any) =>
      [
        ['mensal', p.preco_mensal_centavos],
        ['anual', p.preco_anual_centavos],
      ]
        .filter(([, v]) => Number(v) > 0)
        .map(([ciclo, v]) => ({
          planoCodigo: p.codigo,
          planoNome: p.nome,
          ciclo,
          valorCentavos: v,
        }))
    );
    return ok({ precos }, config);
  }
  if (method === 'post' && (mr = m(/\/admin\/cobrancas\/([^/]+)\/atualizar$/))) {
    const c = DB.cobrancas.find((x: any) => String(x.id) === mr![1]);
    // No mock, "verificar" simula o pagamento caindo.
    if (c?.status === 'PENDENTE')
      Object.assign(c, { status: 'PAGA', paga_em: nowIso(), taxa_centavos: 80 });
    return ok({ cobranca: c }, config);
  }
  if (method === 'post' && (mr = m(/\/admin\/cobrancas\/([^/]+)\/reembolsar$/))) {
    const c = DB.cobrancas.find((x: any) => String(x.id) === mr![1]);
    if (c) Object.assign(c, { status: 'REEMBOLSADA', reembolsada_em: nowIso() });
    return ok({ cobranca: c }, config);
  }
  if (method === 'post' && (mr = m(/\/admin\/cobrancas\/([^/]+)\/marcar-paga$/))) {
    const c = DB.cobrancas.find((x: any) => String(x.id) === mr![1]);
    if (c?.status === 'PENDENTE')
      Object.assign(c, {
        status: 'PAGA',
        paga_em: body?.pagaEm || nowIso(),
        taxa_centavos: 0,
        baixa_manual_forma: body?.forma,
        baixa_manual_observacao: body?.observacao,
      });
    return ok({ cobranca: c }, config);
  }
  if (method === 'post' && (mr = m(/\/admin\/cobrancas\/([^/]+)\/estornar-baixa$/))) {
    const c = DB.cobrancas.find((x: any) => String(x.id) === mr![1]);
    if (c?.baixa_manual_forma)
      Object.assign(c, {
        status: 'PENDENTE',
        paga_em: null,
        baixa_manual_forma: null,
        baixa_manual_observacao: null,
      });
    return ok({ cobranca: c }, config);
  }
  // Cobrança recorrente de um produtor (modo mock: uma fatura em aberto e um aviso entregue).
  if ((mr = m(/\/admin\/users\/([^/]+)\/recorrencia(\/[a-z]+)?$/))) {
    const sub = mr[2];
    if (method === 'post' && sub === '/fatura')
      return ok(
        { criada: true, link_pagamento: 'https://painel.curraltech.com/pagar/mock' },
        config
      );
    if (method === 'post' && sub === '/cancelar') return ok({ cancelada: true }, config);
    return ok(recorrenciaDeMentira(), config);
  }
  if (method === 'post' && m(/\/admin\/faturas\/([^/]+)\/reenviar$/))
    return ok({ enfileirada: true }, config);
  if (method === 'get' && (mr = m(/\/admin\/cobrancas\/([^/]+)$/))) {
    const c = DB.cobrancas.find((x: any) => String(x.id) === mr![1]);
    if (!c) fail(404, 'Cobrança não encontrada', config);
    return ok({ cobranca: c }, config);
  }
  if (method === 'get' && path.endsWith('/admin/cobrancas')) {
    let list = [...DB.cobrancas];
    if (params.status) list = list.filter((c) => c.status === params.status);
    if (params.busca) {
      const b = String(params.busca).toLowerCase();
      list = list.filter((c) =>
        [c.produtor_nome, c.cliente_nome, c.descricao].some((t) => t?.toLowerCase().includes(b))
      );
    }
    const resumo = ['PENDENTE', 'PAGA', 'EXPIRADA', 'REEMBOLSADA'].map((status) => {
      const doStatus = DB.cobrancas.filter((c: any) => c.status === status);
      return {
        status,
        quantidade: doStatus.length,
        valorCentavos: doStatus.reduce((s: number, c: any) => s + c.valor_centavos, 0),
      };
    });
    return ok({ ...paginate(list, params, 'cobrancas'), resumo }, config);
  }
  if (method === 'post' && path.endsWith('/admin/cobrancas')) {
    const id = nextId(DB.cobrancas);
    const plano = DB.plans.find((p: any) => p.codigo === body.planoCodigo);
    const user = DB.users.find((u: any) => String(u.id) === String(body.produtorUserId));
    const valor =
      body.valorCentavos ??
      (body.ciclo === 'anual' ? plano?.preco_anual_centavos : plano?.preco_mensal_centavos);
    const chave = `mock${id}`;
    const nova = {
      ...cobrancasExemplo[cobrancasExemplo.length - 1],
      id,
      token_publico: tokenDeMentira(id),
      link_pagamento: `https://painel.curraltech.com/pagar/${tokenDeMentira(id)}`,
      provedor_id: body.metodo === 'CARTAO' ? null : `pix_char_${chave}`,
      metodo: body.metodo,
      status: 'PENDENTE',
      valor_centavos: valor,
      descricao: body.descricao ?? null,
      plano_id: plano?.id ?? null,
      plano_nome: plano?.nome ?? null,
      plano_codigo: plano?.codigo ?? null,
      ciclo: body.ciclo ?? null,
      produtor_user_id: user ? Number(user.id) : null,
      produtor_nome: user?.name ?? null,
      cliente_nome: body.cliente?.nome || user?.name || null,
      cliente_telefone: user?.phone ?? null,
      cliente_documento: body.cliente?.documento ? '***.456.789-**' : null,
      pix_copia_cola: body.metodo === 'CARTAO' ? null : brCodeDeMentira(chave),
      pix_qrcode_base64: body.metodo === 'PIX' ? qrDeMentira(chave) : null,
      boleto_linha_digitavel:
        body.metodo === 'BOLETO' ? '23793.38128 60082.677713 52000.063304 1 99990000045000' : null,
      boleto_url:
        body.metodo === 'BOLETO' ? `https://app.abacatepay.com/boleto/${chave}.pdf` : null,
      parcelas_max: body.parcelasMax ?? 12,
      taxa_centavos: null,
      comprovante_url: null,
      paga_em: null,
      reembolsada_em: null,
      expira_em: new Date(Date.now() + 3 * 86400_000).toISOString(),
      created_at: nowIso(),
    };
    DB.cobrancas.unshift(nova);
    return ok({ cobranca: nova }, config, 201);
  }

  // ---------- CENTRAL DE ATENDIMENTO ----------
  if (method === 'get' && path.endsWith('/manager/support/agents'))
    return ok({ agents: supportAgents }, config);
  if (method === 'get' && path.endsWith('/manager/support/labels'))
    return ok({ labels: supportLabels }, config);
  if (method === 'get' && path.endsWith('/manager/support/canned'))
    return ok({ canned: supportCanned }, config);
  if (method === 'get' && path.endsWith('/manager/support/producer/otp')) {
    const { producer } = producerByPhone(params.phone);
    if (!producer) return ok({ otp: null }, config);
    const agora = Date.now();
    return ok(
      {
        otp: {
          id: 9000 + producer.id,
          code: String(400000 + ((producer.id * 7919) % 599999)).slice(0, 6),
          createdAt: new Date(agora - 2 * 60_000).toISOString(),
          expiresAt: new Date(agora + 8 * 60_000).toISOString(),
          status: 'active',
        },
      },
      config
    );
  }
  if (method === 'get' && path.endsWith('/manager/support/producer'))
    return ok(producerByPhone(params.phone), config);
  if (method === 'post' && path.endsWith('/manager/support/ai/correct')) {
    const draft = String(body.draft ?? '').trim();
    const corrigido = draft ? draft.charAt(0).toUpperCase() + draft.slice(1) : draft;
    return ok({ corrected: /[.!?]$/.test(corrigido) ? corrigido : `${corrigido}.` }, config);
  }
  if (method === 'get' && (mr = m(/\/manager\/support\/messages\/([^/]+)\/media$/))) {
    const txt = `Arquivo da mensagem ${mr[1]} (modo demonstração).`;
    return ok(new Blob([txt], { type: 'text/plain' }), config);
  }
  if ((mr = m(/\/manager\/support\/conversations\/([^/]+)(?:\/(.+))?$/))) {
    const conv = findConv(mr[1]);
    if (!conv) fail(404, 'Conversa não encontrada', config);
    const c = conv.conversation;
    const acao = mr[2] ?? '';

    if (method === 'get' && acao === '') return ok(c, config);
    if (method === 'get' && acao === 'thread')
      return ok(supportThreads[c.id] ?? { conversations: [], messages: [] }, config);
    if (method === 'get' && acao === 'messages') {
      const before = params.before ? Number(params.before) : null;
      const lista = before ? conv.messages.filter((x: any) => x.id < before) : conv.messages;
      return ok({ messages: lista.slice(-20) }, config);
    }
    if (method === 'post' && acao === 'messages') {
      const message = pushOutgoing(conv, {
        content: body.content,
        private: !!body.private,
        client_message_id: body.client_message_id,
      });
      return ok({ message }, config);
    }
    if (method === 'post' && (acao === 'audio' || acao === 'attachment')) {
      const form = body instanceof FormData ? body : null;
      const file = (form?.get(acao === 'audio' ? 'audio' : 'file') as File | null) ?? null;
      const tipo = file?.type ?? '';
      const fileType =
        acao === 'audio' || tipo.startsWith('audio/')
          ? 'audio'
          : tipo.startsWith('image/')
            ? 'image'
            : tipo.startsWith('video/')
              ? 'video'
              : 'file';
      const message = pushOutgoing(conv, {
        content: (form?.get('caption') as string | null) || null,
        client_message_id: form?.get('client_message_id') ?? undefined,
        attachments: [
          {
            id: novoIdMensagem(),
            file_type: fileType,
            data_url: file ? URL.createObjectURL(file) : undefined,
            file_name: file?.name,
            content_type: tipo,
            file_size: file?.size,
          },
        ],
      });
      return ok({ message }, config);
    }
    if (method === 'post' && acao === 'read') {
      c.unread_count = 0;
      return ok({ success: true }, config);
    }
    if (method === 'post' && acao === 'typing') return ok({ ok: true }, config);
    if (method === 'post' && acao === 'toggle_status') {
      c.status = body.status;
      if (body.status === 'resolved') {
        pushOutgoing(conv, {
          content:
            'Seu atendimento foi encerrado. Se precisar de algo, é só mandar uma mensagem por aqui.',
          sender: { id: 900, name: 'Assistente Curral Tech', type: 'agent_bot' },
        });
      }
      recalcularConversa(conv);
      return ok({ success: true, current_status: c.status }, config);
    }
    if (method === 'post' && acao === 'assignments') {
      if (body.unassign) {
        c.meta.assignee = null;
      } else {
        c.meta.assignee = { id: ME.id, name: ME.name };
        if (c.status === 'pending') c.status = 'open';
      }
      return ok({ success: true, assignee: c.meta.assignee }, config);
    }
    if (method === 'post' && acao === 'transfer') {
      const a = supportAgents.find((x) => x.chatwootUserId === Number(body.assignee_id));
      if (!a) fail(404, 'Atendente não encontrado', config);
      c.meta.assignee = { id: a!.chatwootUserId, name: a!.name };
      return ok({ success: true, assignee: c.meta.assignee }, config);
    }
    if (method === 'post' && acao === 'accept-transfer') {
      c.meta.assignee = { id: ME.id, name: ME.name };
      delete c.custom_attributes.transfer_from;
      delete c.custom_attributes.transfer_to;
      delete c.custom_attributes.transfer_at;
      if (c.status === 'pending') c.status = 'open';
      return ok({ success: true }, config);
    }
    if (method === 'post' && acao === 'labels') {
      c.labels = Array.isArray(body.labels) ? body.labels : [];
      return ok({ payload: c.labels }, config);
    }
    if (method === 'post' && acao === 'pin') {
      c.custom_attributes = { ...(c.custom_attributes || {}), pinned: !!body.pinned };
      return ok({ success: true }, config);
    }
    if (method === 'post' && acao === 'ai/suggest') {
      const nome = String(c.meta?.sender?.name ?? '').split(' ')[0] || 'tudo bem';
      return ok(
        {
          suggestion: `Oi, ${nome}! Obrigado por aguardar. Vou verificar isso agora mesmo e já te retorno por aqui.`,
        },
        config
      );
    }
  }
  if (method === 'get' && path.endsWith('/manager/support/conversations')) {
    const status = params.status ? String(params.status) : null;
    const lista = DB.support
      .filter((c: any) => (status && status !== 'all' ? c.conversation.status === status : true))
      .filter((c: any) =>
        params.assignee_type === 'me'
          ? c.conversation.meta?.assignee?.id === ME.id
          : params.assignee_type === 'unassigned'
            ? !c.conversation.meta?.assignee
            : true
      )
      .map((c: any) => c.conversation);
    return ok({ meta: supportMeta(), conversations: lista }, config);
  }

  // ---------- EQUIPE ----------
  if (method === 'get' && path.endsWith('/manager/team/roles'))
    return ok({ roles: teamRoles }, config);
  if (method === 'get' && path.endsWith('/manager/team')) return ok({ managers: DB.team }, config);
  if (method === 'post' && path.endsWith('/manager/team')) {
    const roles: string[] = Array.isArray(body.roles) ? body.roles : [];
    const manager = {
      id: nextId(DB.team),
      name: body.name,
      email: body.email,
      roles,
      permissions: permissoesDosCargos(roles),
      is_active: true,
      must_change_password: true,
      created_at: nowIso(),
    };
    DB.team.push(manager);
    return ok({ manager }, config, 201);
  }
  if ((mr = m(/\/manager\/team\/([^/]+)$/))) {
    const t = DB.team.find((x: any) => String(x.id) === mr![1]);
    if (!t) fail(404, 'Membro não encontrado', config);
    if (method === 'patch') {
      Object.assign(t, body);
      if (Array.isArray(body.roles)) t.permissions = permissoesDosCargos(body.roles);
      return ok({ manager: t }, config);
    }
    if (method === 'delete') {
      t.is_active = false;
      return ok({ success: true }, config);
    }
  }

  // ---------- CONTRATAÇÕES ----------
  if (method === 'get' && path.endsWith('/manager/careers/jobs')) {
    const jobs = [...DB.jobs]
      .map((j: any) => ({
        ...j,
        candidaturasCount: DB.applications.filter((a: any) => a.vagaId === j.id).length,
      }))
      .sort((a: any, b: any) => a.ordem - b.ordem);
    return ok({ jobs }, config);
  }
  if (method === 'post' && path.endsWith('/manager/careers/jobs')) {
    const titulo = String(body.titulo ?? '');
    const job = {
      ...body,
      id: nextId(DB.jobs),
      slug:
        body.slug ||
        titulo
          .toLowerCase()
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, ''),
      candidaturasCount: 0,
      createdAt: nowIso(),
    };
    DB.jobs.push(job);
    return ok({ job }, config, 201);
  }
  if ((mr = m(/\/manager\/careers\/jobs\/([^/]+)$/))) {
    const idx = DB.jobs.findIndex((x: any) => String(x.id) === mr![1]);
    if (idx < 0) fail(404, 'Vaga não encontrada', config);
    if (method === 'put') {
      DB.jobs[idx] = { ...DB.jobs[idx], ...body };
      return ok({ job: DB.jobs[idx] }, config);
    }
    if (method === 'delete') {
      DB.jobs.splice(idx, 1);
      return ok({ success: true }, config);
    }
  }
  if (method === 'get' && (mr = m(/\/manager\/careers\/applications\/([^/]+)\/cv$/))) {
    const a = DB.applications.find((x: any) => String(x.id) === mr![1]);
    if (!a) fail(404, 'Candidatura não encontrada', config);
    const txt = `Currículo de ${a.nome}\n${a.email} | ${a.whatsapp}\n\n(Arquivo de demonstração do modo mock.)`;
    return ok({ url: URL.createObjectURL(new Blob([txt], { type: 'text/plain' })) }, config);
  }
  if (method === 'patch' && (mr = m(/\/manager\/careers\/applications\/([^/]+)$/))) {
    const a = DB.applications.find((x: any) => String(x.id) === mr![1]);
    if (a) a.status = body.status;
    return ok({ success: true }, config);
  }
  if (method === 'get' && path.endsWith('/manager/careers/applications')) {
    let list = [...DB.applications];
    if (params.vagaId != null && params.vagaId !== '')
      list = list.filter((a) => String(a.vagaId) === String(params.vagaId));
    if (params.tipo) list = list.filter((a) => a.tipo === params.tipo);
    if (params.status) list = list.filter((a) => a.status === params.status);
    list.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return ok({ applications: list }, config);
  }

  // ---------- ORÇAMENTOS ----------
  if (method === 'get' && path.endsWith('/manager/orcamentos/produtos'))
    return ok({ produtos: [...DB.produtos].sort((a, b) => a.ordem - b.ordem) }, config);
  if (method === 'post' && path.endsWith('/manager/orcamentos/produtos')) {
    const produto = { ...body, id: nextId(DB.produtos) };
    DB.produtos.push(produto);
    return ok({ produto }, config, 201);
  }
  if ((mr = m(/\/manager\/orcamentos\/produtos\/([^/]+)$/))) {
    const idx = DB.produtos.findIndex((x: any) => String(x.id) === mr![1]);
    if (idx < 0) fail(404, 'Produto não encontrado', config);
    if (method === 'put') {
      DB.produtos[idx] = { ...DB.produtos[idx], ...body };
      return ok({ produto: DB.produtos[idx] }, config);
    }
    if (method === 'delete') {
      DB.produtos.splice(idx, 1);
      return ok({ success: true }, config);
    }
  }
  if (method === 'post' && path.endsWith('/manager/orcamentos/frete/cotar')) {
    const itens: any[] = Array.isArray(body.itens) ? body.itens : [];
    const pesoKg = itens.reduce((acc, i) => acc + (i.peso_g || 0) * (i.quantidade || 0), 0) / 1000;
    const base = 28 + pesoKg * 4.2;
    return ok(
      {
        configurado: true,
        cepOrigem: body.cepOrigem || '79002-000',
        opcoes: [
          {
            id: 1,
            nome: 'PAC',
            empresa: 'Correios',
            preco: Math.round(base * 100) / 100,
            prazoDias: 8,
          },
          {
            id: 2,
            nome: 'SEDEX',
            empresa: 'Correios',
            preco: Math.round(base * 1.7 * 100) / 100,
            prazoDias: 3,
          },
          {
            id: 3,
            nome: '.Package',
            empresa: 'Jadlog',
            preco: Math.round(base * 1.15 * 100) / 100,
            prazoDias: 6,
          },
        ],
      },
      config
    );
  }
  if (method === 'get' && path.endsWith('/manager/orcamentos/salvos')) {
    const orcamentos = [...DB.orcamentos]
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      .map(({ dados: _dados, ...resumo }) => resumo);
    return ok({ orcamentos }, config);
  }
  if (method === 'post' && path.endsWith('/manager/orcamentos/salvos')) {
    const orcamento = {
      status: 'rascunho',
      ...body,
      id: nextId(DB.orcamentos),
      createdAt: nowIso(),
      updatedAt: nowIso(),
    };
    DB.orcamentos.push(orcamento);
    return ok({ orcamento }, config, 201);
  }
  if ((mr = m(/\/manager\/orcamentos\/salvos\/([^/]+)$/))) {
    const idx = DB.orcamentos.findIndex((x: any) => String(x.id) === mr![1]);
    if (idx < 0) fail(404, 'Orçamento não encontrado', config);
    if (method === 'get') return ok({ orcamento: DB.orcamentos[idx] }, config);
    if (method === 'put') {
      DB.orcamentos[idx] = { ...DB.orcamentos[idx], ...body, updatedAt: nowIso() };
      return ok({ orcamento: DB.orcamentos[idx] }, config);
    }
    if (method === 'delete') {
      DB.orcamentos.splice(idx, 1);
      return ok({ success: true }, config);
    }
  }

  // ---------- NOTIFICAÇÕES ----------
  // (insights/devices fica antes do bloco DEVICES: o endsWith('/devices') pegaria a rota)
  if (method === 'get' && path.endsWith('/manager/notifications')) {
    const notifications = [...DB.notifications].sort((a, b) =>
      b.createdAt.localeCompare(a.createdAt)
    );
    return ok({ notifications }, config);
  }
  if (method === 'post' && path.endsWith('/manager/notifications')) {
    const notification = {
      images: [],
      items: [],
      link: null,
      force: false,
      sendPush: false,
      minVersion: null,
      targetPropertyId: null,
      startsAt: nowIso(),
      endsAt: null,
      priority: 0,
      ...body,
      id: nextId(DB.notifications),
      readCount: 0,
      lastSent: null,
      lastFailed: null,
      lastDispatchedAt: null,
      createdBy: DB.auth.manager.name,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    };
    DB.notifications.push(notification);
    return ok({ notification }, config, 201);
  }
  if (method === 'post' && (mr = m(/\/manager\/notifications\/([^/]+)\/dispatch$/))) {
    const n = DB.notifications.find((x: any) => String(x.id) === mr![1]);
    if (!n) fail(404, 'Notificação não encontrada', config);
    const sent = deviceInsights.devicesWithPushToken - 7;
    Object.assign(n, { lastSent: sent, lastFailed: 7, lastDispatchedAt: nowIso() });
    return ok({ sent, failed: 7 }, config);
  }
  if (method === 'get' && (mr = m(/\/manager\/notifications\/([^/]+)\/engagement$/))) {
    const n = DB.notifications.find((x: any) => String(x.id) === mr![1]);
    if (!n?.lastSent) return ok({ available: false, received: 0, opened: 0 }, config);
    return ok(
      {
        available: true,
        received: n.lastSent - (n.lastFailed ?? 0),
        opened: Math.round(n.lastSent * 0.37),
      },
      config
    );
  }
  if (method === 'delete' && (mr = m(/\/manager\/notifications\/([^/]+)$/))) {
    DB.notifications = DB.notifications.filter((x: any) => String(x.id) !== mr![1]);
    return ok({ success: true }, config);
  }

  // ---------- fallback ----------
  console.warn('[mockAdapter] rota não mapeada:', method.toUpperCase(), path);
  fail(404, `Mock não mapeado: ${method.toUpperCase()} ${path}`, config);
};
